/**
 * LHS Control Dashboard
 * 
 * Ops view: "Where are we winning / losing?"
 * Deep insights, contractor performance, cost control
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Clock,
  PoundSterling, Users, Briefcase, RefreshCw, ChevronDown,
  BarChart3, AlertCircle, Shield, Target, MapPin
} from 'lucide-react';
import axios from 'axios';
import Layout from '@/components/Layout';
import MarketplaceDashboard, { CoverageMatrixWidget } from '@/components/MarketplaceDashboard';
import ControlEngineDashboard from '@/components/ControlEngineDashboard';

const API = process.env.REACT_APP_BACKEND_URL;

// ============================================================
// 🎨 DESIGN TOKENS  
// ============================================================
const tokens = {
  primary: "#174C86",
  bg: "#F4F6F8",
  card: "#FFFFFF",
  textMain: "#0F172A",
  textMuted: "#64748B",
  border: "#E2E8F0",
  success: "#10B981",
  warning: "#F59E0B",
  error: "#EF4444",
};

// ============================================================
// 🧱 COMPONENTS
// ============================================================

const RiskBadge = ({ level }) => {
  const styles = {
    low: "bg-emerald-100 text-emerald-700",
    medium: "bg-amber-100 text-amber-700",
    high: "bg-red-100 text-red-700",
  };
  
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium uppercase ${styles[level] || styles.low}`}>
      {level}
    </span>
  );
};

const MetricCard = ({ label, value, subtitle, icon: Icon, variant = "default" }) => {
  const variants = {
    default: "border-[#E2E8F0]",
    success: "border-emerald-300 bg-emerald-50/50",
    warning: "border-amber-300 bg-amber-50/50",
    danger: "border-red-300 bg-red-50/50",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white border-2 ${variants[variant]} rounded-2xl p-4`}
    >
      <div className="flex justify-between items-start">
        <div>
          <p className="text-xs font-medium text-[#64748B] uppercase">{label}</p>
          <p className="text-2xl font-bold text-[#0F172A] mt-1">{value}</p>
          {subtitle && <p className="text-xs text-[#64748B] mt-0.5">{subtitle}</p>}
        </div>
        {Icon && <Icon className="h-5 w-5 text-[#64748B]" />}
      </div>
    </motion.div>
  );
};

const ContractorRow = ({ contractor, index, expanded, onToggle }) => {
  const isHighRisk = contractor.risk_level === "high";
  const isMediumRisk = contractor.risk_level === "medium";
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      className={`border-2 rounded-xl overflow-hidden ${
        isHighRisk ? "border-red-200 bg-red-50/30" : 
        isMediumRisk ? "border-amber-200 bg-amber-50/30" : 
        "border-[#E2E8F0]"
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
            isHighRisk ? "bg-red-500" : isMediumRisk ? "bg-amber-500" : "bg-[#174C86]"
          }`}>
            {contractor.contractor_name?.charAt(0) || "?"}
          </div>
          <div className="text-left">
            <div className="font-semibold text-[#0F172A]">{contractor.contractor_name}</div>
            <div className="text-xs text-[#64748B]">
              {contractor.jobs} jobs • {contractor.completed} completed
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className={`font-bold ${
              contractor.avg_variance > 10 ? "text-red-600" : 
              contractor.avg_variance > 5 ? "text-amber-600" : 
              "text-[#0F172A]"
            }`}>
              {contractor.avg_variance > 0 ? "+" : ""}{contractor.avg_variance}%
            </div>
            <div className="text-xs text-[#64748B]">variance</div>
          </div>
          <RiskBadge level={contractor.risk_level} />
          <motion.div animate={{ rotate: expanded ? 180 : 0 }}>
            <ChevronDown className="h-5 w-5 text-[#64748B]" />
          </motion.div>
        </div>
      </button>
      
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-2 border-t border-[#E2E8F0] grid grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-[#64748B]">Completion Rate</div>
                <div className="font-semibold text-[#0F172A]">{contractor.completion_rate}%</div>
              </div>
              <div>
                <div className="text-xs text-[#64748B]">Quote Approval</div>
                <div className="font-semibold text-[#0F172A]">{contractor.quote_approval_rate}%</div>
              </div>
              <div>
                <div className="text-xs text-[#64748B]">Total Invoiced</div>
                <div className="font-semibold text-[#0F172A]">£{contractor.total_invoiced?.toLocaleString() || 0}</div>
              </div>
              <div>
                <div className="text-xs text-[#64748B]">Over-Quote</div>
                <div className={`font-semibold ${contractor.over_quote_count > 0 ? "text-red-600" : "text-emerald-600"}`}>
                  {contractor.over_quote_count} invoices
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const VarianceRow = ({ item, index }) => {
  const isOver = item.variance_percent > 10;
  const isUnder = item.variance_percent < -10;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.02 }}
      className={`p-3 rounded-xl border-2 ${
        isOver ? "border-red-200 bg-red-50/50" : 
        isUnder ? "border-blue-200 bg-blue-50/50" : 
        "border-[#E2E8F0]"
      }`}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="font-medium text-[#0F172A]">{item.job_reference}</div>
          <div className="text-xs text-[#64748B]">{item.site_name} • {item.contractor_name}</div>
        </div>
        <div className="text-right">
          <div className={`font-bold ${isOver ? "text-red-600" : isUnder ? "text-blue-600" : "text-[#0F172A]"}`}>
            {item.variance_percent > 0 ? "+" : ""}{item.variance_percent?.toFixed(1)}%
          </div>
          <div className="text-xs text-[#64748B]">
            £{item.quote_total?.toLocaleString() || 0} → £{item.invoice_total?.toLocaleString() || 0}
          </div>
        </div>
      </div>
      {item.flags?.length > 0 && (
        <div className="mt-2 flex gap-1">
          {item.flags.map(flag => (
            <span key={flag} className={`text-xs px-2 py-0.5 rounded ${
              flag === "OVER_QUOTE" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"
            }`}>
              {flag.replace("_", " ")}
            </span>
          ))}
        </div>
      )}
    </motion.div>
  );
};

// ============================================================
// 🎯 MAIN COMPONENT
// ============================================================

export default function LhsControlDashboard() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [variances, setVariances] = useState(null);
  const [expandedContractor, setExpandedContractor] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [error, setError] = useState(null);

  const token = localStorage.getItem('token') || localStorage.getItem('wyldecrest_token');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [overviewRes, contractorsRes, varianceRes] = await Promise.all([
        axios.get(`${API}/api/wyldecrest/reports/overview`, { headers }),
        axios.get(`${API}/api/wyldecrest/reports/contractors`, { headers }),
        axios.get(`${API}/api/wyldecrest/reports/variance`, { headers })
      ]);
      
      setOverview(overviewRes.data);
      setContractors(contractorsRes.data.contractors || []);
      setVariances(varianceRes.data);
    } catch (err) {
      console.error("Load error:", err);
      setError(err.response?.data?.detail || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const highRiskContractors = contractors.filter(c => c.risk_level === "high");
  const overQuoteInvoices = variances?.variances?.filter(v => v.variance_percent > 10) || [];

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#F4F6F8] flex items-center justify-center">
          <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }}>
            <RefreshCw className="h-8 w-8 text-[#174C86]" />
          </motion.div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-[#F4F6F8]">
        {/* Header */}
        <div className="bg-white border-b-2 border-[#E2E8F0] px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl font-bold text-[#0F172A]">LHS Control Centre</h1>
              <p className="text-sm text-[#64748B]">Operations & Financial Control</p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={loadData}
              className="p-2 rounded-xl bg-[#174C86] text-white hover:bg-[#0f3a6a] transition-colors"
            >
              <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </motion.button>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-1 mt-4 flex-wrap">
          {[
            { id: "overview", label: "Overview" },
            { id: "sla", label: "SLA Control" },
            { id: "marketplace", label: "Marketplace" },
            { id: "contractors", label: "Contractors" },
            { id: "variance", label: "Variance" }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === tab.id
                  ? "bg-[#174C86] text-white"
                  : "text-[#64748B] hover:bg-gray-100"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <main className="p-6 space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* Alerts */}
        {(highRiskContractors.length > 0 || overQuoteInvoices.length > 0) && (
          <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4">
            <div className="flex items-center gap-2 text-amber-700 font-semibold mb-2">
              <AlertCircle className="h-5 w-5" />
              Attention Required
            </div>
            <div className="space-y-1 text-sm text-amber-700">
              {highRiskContractors.length > 0 && (
                <p>• {highRiskContractors.length} high-risk contractor(s) flagged</p>
              )}
              {overQuoteInvoices.length > 0 && (
                <p>• {overQuoteInvoices.length} invoice(s) over-quote threshold</p>
              )}
            </div>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard
                label="Total Jobs"
                value={overview?.total_jobs || 0}
                icon={Briefcase}
              />
              <MetricCard
                label="Completed"
                value={overview?.completed || 0}
                subtitle={`${overview?.completed_last_30_days || 0} this month`}
                icon={CheckCircle}
                variant="success"
              />
              <MetricCard
                label="Overdue"
                value={overview?.overdue || 0}
                icon={AlertTriangle}
                variant={overview?.overdue > 0 ? "danger" : "success"}
              />
              <MetricCard
                label="SLA Compliance"
                value={`${overview?.sla_compliance_percent || 100}%`}
                subtitle={`${overview?.sla_breached || 0} breaches`}
                icon={Shield}
                variant={overview?.sla_compliance_percent >= 90 ? "success" : "warning"}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <MetricCard
                label="Total Spend"
                value={`£${(overview?.total_spend || 0).toLocaleString()}`}
                icon={PoundSterling}
              />
              <MetricCard
                label="Average Variance"
                value={`${overview?.avg_variance_percent || 0}%`}
                subtitle={`${overview?.over_quote_invoices || 0} over-quote`}
                icon={BarChart3}
                variant={overview?.avg_variance_percent > 10 ? "warning" : "default"}
              />
              <MetricCard
                label="Active Contractors"
                value={contractors.length}
                subtitle={`${highRiskContractors.length} high-risk`}
                icon={Users}
                variant={highRiskContractors.length > 0 ? "warning" : "default"}
              />
            </div>
          </>
        )}

        {/* SLA Control Tab */}
        {activeTab === "sla" && (
          <div className="space-y-4">
            <ControlEngineDashboard />
          </div>
        )}

        {/* Marketplace Tab */}
        {activeTab === "marketplace" && (
          <div className="space-y-4">
            <MarketplaceDashboard />
            <CoverageMatrixWidget />
          </div>
        )}

        {/* Contractors Tab */}
        {activeTab === "contractors" && (
          <div className="space-y-3">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-[#0F172A]">Contractor Performance</h2>
              <span className="text-sm text-[#64748B]">{contractors.length} contractors</span>
            </div>
            
            {contractors.length > 0 ? (
              contractors.map((c, i) => (
                <ContractorRow
                  key={c.contractor_id}
                  contractor={c}
                  index={i}
                  expanded={expandedContractor === c.contractor_id}
                  onToggle={() => setExpandedContractor(
                    expandedContractor === c.contractor_id ? null : c.contractor_id
                  )}
                />
              ))
            ) : (
              <div className="text-center py-8 text-[#64748B]">
                No contractor data available
              </div>
            )}
          </div>
        )}

        {/* Variance Tab */}
        {activeTab === "variance" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold text-[#0F172A]">Invoice Variance Analysis</h2>
              <div className="flex gap-2 text-sm">
                <span className="px-2 py-1 bg-red-100 text-red-700 rounded">
                  Over: {variances?.over_quote_count || 0}
                </span>
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded">
                  On Target: {variances?.on_target_count || 0}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              {variances?.variances?.slice(0, 20).map((v, i) => (
                <VarianceRow key={`${v.job_id}-${i}`} item={v} index={i} />
              ))}
              {(!variances?.variances || variances.variances.length === 0) && (
                <div className="text-center py-8 text-[#64748B]">
                  No variance data available
                </div>
              )}
            </div>
          </div>
        )}
      </main>
      </div>
    </Layout>
  );
}
